/**
 * gameShareService — client wrapper around share-game and accept-game-share.
 */

import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics/ga";

export interface ShareResult {
  email: string;
  share_token: string;
  status: "sent" | "email_failed";
  error?: string;
}

export interface ShareGameResponse {
  sent: number;
  shares: ShareResult[];
}

export interface AcceptShareResponse {
  game_id: string;
  shared_by_user_id: string;
  // game shape mirrors `games` row — keep as unknown for the caller to narrow
  game: Record<string, unknown>;
}

export const gameShareService = {
  async share(
    gameId: string,
    emails: string[],
    message?: string,
  ): Promise<ShareGameResponse> {
    trackEvent("game_share_requested", { game_id: gameId, count: emails.length });
    const { data, error } = await supabase.functions.invoke("share-game", {
      body: {
        game_id: gameId,
        emails,
        message,
        app_url: window.location.origin,
      },
    });
    if (error) {
      trackEvent("game_share_failed", { game_id: gameId, error: error.message });
      throw error;
    }
    trackEvent("game_share_succeeded", { game_id: gameId, sent: data?.sent ?? 0 });
    return data as ShareGameResponse;
  },

  async accept(token: string): Promise<AcceptShareResponse> {
    const { data, error } = await supabase.functions.invoke("accept-game-share", {
      body: { share_token: token },
    });
    if (error) throw error;
    return data as AcceptShareResponse;
  },
};
